/**
 * Custody of the consent envelope's link token between the two onboarding steps that
 * need it.
 *
 * WHY THIS EXISTS AT ALL. Onboarding verifies the client's email early (right after the
 * identity step) and collects the signature at the end. Both address the envelope by its
 * link token, and that token exists in plaintext exactly once — in the response that
 * created the envelope. The server stores only an HMAC of it (`esign/envelope.py`), so it
 * cannot be looked up, reissued, or derived. Something has to hold it across the steps in
 * between, and the server has deliberately made itself unable to.
 *
 * WHY THIS IS NOT THE STORE THAT WAS DELETED. `onboarding/useOnboardingForm.ts` records
 * that a standalone `dova:onboarding` localStorage layer was removed because it was a
 * SECOND copy of durable state the server already held, and the two could drift. This is
 * the inverse: it is the one value the server does not hold, it copies nothing, and
 * nothing reads it as onboarding state.
 *
 * ── THE SECURITY POSITION, STATED ACCURATELY ─────────────────────────────────────────
 * An earlier version of this comment argued the XSS posture was UNCHANGED, because
 * `/esign/:token` and `/me/*` are mounted in the same SPA on the same origin
 * (`main.tsx`), so the token was already readable by script on this origin. The premise
 * is true. **The conclusion does not follow, and the claim was wrong.**
 *
 * Same origin makes the SPATIAL boundary shared. It says nothing about the TEMPORAL one,
 * and this flow enlarges that enormously. Before, no envelope, no token and no live
 * signer session existed until the client entered the signer route at the very end;
 * exposure lasted as long as the signing ceremony. Now, from the moment early
 * verification succeeds, the tab holds BOTH this script-readable token AND a live
 * `HttpOnly` signer-session cookie for the remaining onboarding steps — so a same-origin
 * script injection on any page in between can read the token and issue a credentialed
 * `/document` fetch. That window is new. It is bounded by the session TTL
 * (`esign/session.py`, one hour) and by `clearEsignLinkToken` on a completed signature,
 * and it is an accepted cost of not asking the client for a second code — not an absence
 * of cost. Do not restore the "posture is flat" wording.
 *
 * The alternative that removes it is server-side custody (Redis, keyed on the
 * authenticated person), which would make the token non-script-readable. That is a live
 * option, not a rejected one.
 * ─────────────────────────────────────────────────────────────────────────────────────
 *
 * WHY sessionStorage AND NOT localStorage. The token has no expiry of its own, so disk
 * persistence means an indefinitely live bearer credential at rest. sessionStorage bounds
 * it to one tab, which is about one onboarding run — roughly the lifetime of the signer
 * session it unlocks. Closing the tab lands in the recovery path, which tells the client
 * what happened, rather than leaving a credential behind.
 *
 * WHY NOT A COOKIE, which would be HttpOnly and unreadable by script. The `/me` app and
 * the API are different sites, so an API-set cookie would need `SameSite=None; Secure` —
 * a SECOND cookie whose load-bearing attribute is never exercised in dev or CI, because
 * `is_local_dev_or_test()` picks `lax` there. That attribute is exactly what broke every
 * signer in #1177. This trade is about which risk to take, not about one being free.
 *
 * WHY THE KEY NAMES THE COUPLE. A person can belong to more than one client group, and a
 * token is only ever valid for the group whose envelope it addresses. Keyed on the person
 * alone, a token from group A is offered to a run in group B, where it resolves and
 * reports `email_verified` — so the early step believes verification is done, the signing
 * step finds nothing pending for B and mints silently, and the client re-verifies at the
 * end with nothing recording why. The compound key means the wrong token is never even
 * offered. It is the first of two defences: the server independently refuses a token
 * whose signer, group or contact identity does not match this run
 * (`_carried_token_status` in `routers/me.py`), which also catches the case the key
 * cannot see — the client changing their email inside one group.
 *
 * Every accessor swallows exceptions. Safari in private mode and any browser with site
 * data blocked THROW on `sessionStorage` access rather than returning null, and a client
 * whose browser refuses storage must still be able to complete onboarding — they take the
 * recovery path, which is a working path.
 */

const KEY_PREFIX = "dova:esign:link:";

/** A token belongs to one couple AND one person; both are in the key for that reason. */
const key = (coupleId: string, partnerId: string) =>
  `${KEY_PREFIX}${coupleId}:${partnerId}`;

/** The stored link token for this partner's in-flight envelope, if this tab has one. */
export function readEsignLinkToken(
  coupleId: string,
  partnerId: string,
): string | null {
  if (!coupleId || !partnerId) return null;
  try {
    const stored = window.sessionStorage.getItem(key(coupleId, partnerId));
    return stored && stored.trim() ? stored : null;
  } catch {
    return null;
  }
}

/**
 * Remember the token for this partner's envelope.
 *
 * Called at CREATION rather than after verification, deliberately. A client who reloads
 * the verify step mid-code would otherwise lose the address of the envelope whose code is
 * already in their mailbox, and the next call would mint a second one — leaving them
 * holding a code that verifies nothing, with no way to tell why.
 */
export function writeEsignLinkToken(
  coupleId: string,
  partnerId: string,
  token: string,
): void {
  if (!coupleId || !partnerId || !token) return;
  try {
    window.sessionStorage.setItem(key(coupleId, partnerId), token);
  } catch {
    // Storage refused. The caller keeps the token for this render and the flow still
    // completes; only the carry across steps is lost, which is the recovery path.
  }
}

/**
 * Forget this partner's token — it is spent, or it no longer belongs to this run.
 *
 * Also drops that link token's session-token entry (`dova:esign:session:<link-token>`,
 * below), if this tab holds one. That entry is keyed by the link token's own VALUE, so
 * clearing the link-token entry alone leaves it behind, still reproducing the (now
 * abandoned) link token as its key — a second, longer-lived copy of a spent bearer
 * credential. Read the value BEFORE removing it: once the link-token entry is gone, there
 * is no way back to which session entry corresponded to it.
 */
export function clearEsignLinkToken(coupleId: string, partnerId: string): void {
  if (!coupleId || !partnerId) return;
  const linkToken = readEsignLinkToken(coupleId, partnerId);
  try {
    window.sessionStorage.removeItem(key(coupleId, partnerId));
  } catch {
    // Nothing to do: an unreadable store is also an unwritable one.
  }
  if (linkToken) clearEsignSessionToken(linkToken);
}

/**
 * Forget a token by its VALUE, wherever it is filed.
 *
 * For the signer app, which is the code that knows a signature has just succeeded and so
 * knows the token is spent — but which is reached by URL and holds no couple or partner
 * id to build a key from. Matching on the value is exact: this is the token that was just
 * used, and the entry storing it is by definition the one to drop.
 *
 * Without this the spent token survives the whole way through handoff to tab close. It
 * was previously cleared only if the client happened to revisit the consent step
 * afterwards, which the ordinary path never does — so on the happy path it was never
 * cleared at all, which is the wrong way round for a credential whose exposure window
 * this flow already enlarged.
 *
 * Also drops the session-token entry keyed by this same value (below) — the caller
 * already has the exact link token in hand here, so this is a direct key computation,
 * not the by-value scan the link-token entry itself needs.
 */
export function clearEsignLinkTokenByValue(token: string): void {
  if (!token) return;
  try {
    const spent: string[] = [];
    for (let index = 0; index < window.sessionStorage.length; index += 1) {
      const candidate = window.sessionStorage.key(index);
      if (
        candidate?.startsWith(KEY_PREFIX) &&
        window.sessionStorage.getItem(candidate) === token
      ) {
        spent.push(candidate);
      }
    }
    // Collected first, removed second: removing during the scan reindexes the store and
    // silently skips entries.
    for (const candidate of spent) {
      window.sessionStorage.removeItem(candidate);
    }
  } catch {
    // See above — an unreadable store is also an unwritable one.
  }
  clearEsignSessionToken(token);
}

/**
 * Custody of the SECOND credential this flow mints: the signer session `/verify` returns.
 *
 * WHY THIS EXISTS. `esign_session` was cookie-only, and that cookie is cross-site (the
 * app and API are different registrable domains). `SameSite=None; Secure` makes it
 * ELIGIBLE to be sent cross-site; it does not make a browser's own third-party-cookie
 * policy store it in the first place, and Safari's ITP does not, by default — reproduced
 * against a live client stuck re-entering a code that kept "working" and then being asked
 * for again. See `specifications/esign_session_credential_header_spec.md`. The server now
 * also returns the plaintext token in `/verify`'s JSON body; this is where a tab holds it
 * so it can be sent back as the `Dova-Esign-Session` header on every later gated call.
 *
 * WHY KEYED BY THE LINK TOKEN, NOT `(coupleId, partnerId)` like the functions above. This
 * credential is needed by the standalone signer app (`/esign/:token`), which — as the
 * comment on `clearEsignLinkTokenByValue` explains — is reached by URL and holds no couple
 * or partner id to build that key from. The envelope's own link token is the one value
 * every caller already has in hand, onboarding-embedded or standalone alike.
 *
 * Same sessionStorage/try-catch shape as above, for the same reason: bounded to one tab,
 * dies with the tab, and a client whose browser refuses storage still has a working
 * fallback — the cookie the server keeps setting alongside this, best-effort.
 */

const SESSION_KEY_PREFIX = "dova:esign:session:";

const sessionKey = (esignToken: string) => `${SESSION_KEY_PREFIX}${esignToken}`;

/** The stored signer-session credential for this envelope's link token, if this tab has one. */
export function readEsignSessionToken(esignToken: string): string | null {
  if (!esignToken) return null;
  try {
    const stored = window.sessionStorage.getItem(sessionKey(esignToken));
    return stored && stored.trim() ? stored : null;
  } catch {
    return null;
  }
}

/** Remember the session token `/verify` returned for this envelope's link token. */
export function writeEsignSessionToken(esignToken: string, sessionToken: string): void {
  if (!esignToken || !sessionToken) return;
  try {
    window.sessionStorage.setItem(sessionKey(esignToken), sessionToken);
  } catch {
    // Storage refused. The caller keeps the token for this render and the flow still
    // completes on the cookie fallback; only the header-carried retry across a reload
    // is lost.
  }
}

/** Forget the session token for this envelope's link token — spent, or no longer live. */
export function clearEsignSessionToken(esignToken: string): void {
  if (!esignToken) return;
  try {
    window.sessionStorage.removeItem(sessionKey(esignToken));
  } catch {
    // Nothing to do: an unreadable store is also an unwritable one.
  }
}

/**
 * The `Dova-Esign-Session` header for this envelope's link token, or `{}` if this tab
 * holds no session token yet (before verification, or if storage refused the write).
 *
 * Spread into a fetch's `headers` on every gated call — `/document`, `/sign` — ALONGSIDE
 * `credentials: "include"`, never instead of it: the cookie stays as a same-request-shape
 * fallback for browsers where the cross-site cookie does survive.
 */
export function esignSessionHeaders(esignToken: string): HeadersInit {
  const token = readEsignSessionToken(esignToken);
  return token ? { "Dova-Esign-Session": token } : {};
}
